import { useEffect, useState } from 'react'
import { useAgents } from '@/hooks/useAgents'
import { useRealtimeStore } from '@/hooks/useRealtime'
import { Bot, X, RefreshCw, Activity, Cpu, MemoryStick } from 'lucide-react'
import type { AgentStatus } from '@/lib/types'
import { clsx } from 'clsx'
import PageHeader from '@/components/ui/PageHeader'

function StatusDot({ status }: { status: string }) {
  const cls = status === 'active' ? 'status-active' : status === 'error' ? 'status-error' : 'status-idle'
  return <span className={cls} aria-hidden />
}

function AgentModal({ agent, onClose }: { agent: AgentStatus; onClose: () => void }) {
  const { analysis, runAnalysis, analyzing } = useAgents()

  useEffect(() => { if (!analysis) runAnalysis() }, []) // eslint-disable-line

  const agentExplanation = analysis?.optimization.rationale ?? analysis?.behavioral.narrative ?? ''

  return (
    <div role="dialog" aria-modal="true" aria-label={`${agent.name} details`}
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-lg p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue/10 border border-blue/20 flex items-center justify-center">
              <Bot size={20} className="text-blue-light" aria-hidden />
            </div>
            <div>
              <h2 className="font-bold text-text-primary">{agent.name}</h2>
              <p className="text-xs text-text-muted">{agent.type.replace('_', ' ')}</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Performance', value: `${(agent.performance ?? 0).toFixed(1)}%` },
            { label: 'Decisions today', value: agent.decisions_today },
            { label: 'Uptime', value: agent.uptime },
          ].map(s => (
            <div key={s.label} className="bg-surface-DEFAULT rounded-lg p-3 text-center">
              <p className="text-xs text-text-muted mb-1">{s.label}</p>
              <p className="text-sm font-bold text-text-primary">{s.value}</p>
            </div>
          ))}
        </div>

        {/* System metrics */}
        <div className="space-y-2.5 mb-5">
          <div className="flex items-center gap-2 text-sm">
            <Cpu size={14} className="text-text-muted" aria-hidden />
            <span className="text-text-secondary">CPU</span>
            <div className="flex-1 h-1.5 bg-surface-hover rounded-full mx-2">
              <div className="h-full bg-blue rounded-full" style={{ width: `${agent.cpu_usage}%` }} />
            </div>
            <span className="text-xs text-text-muted font-mono w-8 text-right">{(agent.cpu_usage ?? 0).toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <MemoryStick size={14} className="text-text-muted" aria-hidden />
            <span className="text-text-secondary">RAM</span>
            <span className="ml-auto text-xs text-text-muted font-mono">{(agent.memory_mb ?? 0).toFixed(0)} MB</span>
          </div>
        </div>

        {/* Current task */}
        <div className="bg-surface-DEFAULT rounded-lg p-3 mb-5">
          <p className="text-xs text-text-muted mb-1 uppercase tracking-wider">Current task</p>
          <p className="text-sm text-text-primary">{agent.current_task}</p>
        </div>

        {/* Portfolio-level rationale — this is ONE shared analysis rationale,
            not per-agent reasoning; label it honestly. */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-purple-light" aria-hidden />
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">Portfolio-level rationale</p>
          </div>
          <p className="text-[11px] text-text-muted mb-2">
            Based on the overall portfolio analysis — not this agent's individual reasoning.
          </p>
          {analyzing ? (
            <p className="text-sm text-text-muted">Generating explanation…</p>
          ) : agentExplanation ? (
            <p className="text-sm text-text-secondary leading-relaxed">{agentExplanation}</p>
          ) : (
            <p className="text-sm text-text-muted">Run a full analysis to see the portfolio-level rationale.</p>
          )}
        </div>

        <button onClick={onClose} className="btn-secondary w-full mt-5">Close</button>
      </div>
    </div>
  )
}

function AgentCard({ agent, onDetails }: { agent: AgentStatus; onDetails: () => void }) {
  return (
    <article
      className="card-hover p-5 flex flex-col gap-3"
      aria-label={`${agent.name} status card`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue/10 border border-blue/20 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-blue-light">{agent.acronym}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{agent.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <StatusDot status={agent.status} />
              <span className="text-xs text-text-muted capitalize">{agent.status}</span>
            </div>
          </div>
        </div>
        {/* Performance score */}
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-text-primary">{(agent.performance ?? 0).toFixed(0)}%</p>
          <p className="text-xs text-text-muted">score</p>
        </div>
      </div>

      <p className="text-xs text-text-secondary leading-relaxed">{agent.current_task}</p>

      <div className="flex items-center justify-between pt-1 border-t border-border">
        <span className="text-xs text-text-muted">{agent.decisions_today} decisions today</span>
        <button onClick={onDetails} className="btn-ghost text-xs py-1 px-2 gap-1">
          Details
        </button>
      </div>
    </article>
  )
}

export default function AgentsPage() {
  const { agents, loading, fetchStatus, runAnalysis, analyzing } = useAgents()
  const rtAgents = useRealtimeStore(s => s.agentEvents)
  const [selected, setSelected] = useState<AgentStatus | null>(null)

  useEffect(() => { fetchStatus() }, []) // eslint-disable-line

  // Prefer live WS data if available
  const displayAgents = rtAgents.length > 0 ? rtAgents : agents

  return (
    <div className="space-y-6 animate-fade-in">
      {selected && <AgentModal agent={selected} onClose={() => setSelected(null)} />}

      <PageHeader
        title="AI Agents"
        subtitle={`${displayAgents.length} agents active`}
        actions={
          <>
            <button onClick={() => fetchStatus()} className="btn-secondary gap-1.5 text-xs" disabled={loading}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
              Refresh
            </button>
            <button onClick={() => runAnalysis()} className="btn-primary gap-1.5 text-xs" disabled={analyzing}>
              <Bot size={13} aria-hidden />
              {analyzing ? 'Analysing…' : 'Run Analysis'}
            </button>
          </>
        }
      />

      {loading && displayAgents.length === 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-40" />
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {displayAgents.map(a => (
            <AgentCard key={a.id} agent={a} onDetails={() => setSelected(a)} />
          ))}
        </div>
      )}

      {/* System health */}
      <div className="card p-5">
        <h2 className="panel-title mb-4">Agent Communications</h2>
        <div className="space-y-2.5" aria-live="polite" aria-label="Agent activity feed">
          {displayAgents.slice(0, 5).map((a, i) => (
            <div key={i} className="flex items-start gap-3 text-sm">
              <span className="status-active mt-1.5 shrink-0" aria-hidden />
              <div className="min-w-0">
                <span className="font-mono text-xs text-blue-light">[{a.acronym}]</span>
                <span className="text-text-secondary ml-2 truncate">{a.current_task}</span>
              </div>
            </div>
          ))}
          {displayAgents.length === 0 && (
            <p className="text-text-muted text-sm">No agent activity yet.</p>
          )}
        </div>
      </div>
    </div>
  )
}
